import React, { useState } from "react";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Link } from "@samskip/frontend-components";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "@assets/hooks";
import { Company } from "@assets/types";
import { Popover, CompanyPickerSearchInput, WithDebounce } from "@components";
import UserService from "@services/UserService";
import { switchCompany } from "@state/slices/userProfileSlice";

type CompanyPickerProps = {
  company: Company;
  userIsEmployee: boolean;
  userCompanies: Company[] | null;
};

const CompanyPicker = ({
  company,
  userIsEmployee,
  userCompanies,
}: CompanyPickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>(userCompanies || []);
  const [isCompanySearchLoading, setIsCompanySearchLoading] = useState(false);
  const isActive = isCompanySearchLoading || !!companies.length;
  const dispatch = useAppDispatch();

  const toggleOpen = () => {
    setIsOpen((oldOpen) => !oldOpen);
    setCompanies(userCompanies || []);
  };

  const closePopover = () => {
    setIsOpen(false);
    setCompanies(userCompanies || []);
  };

  const onSelectCompany = (company: Company) => {
    dispatch(switchCompany(company.PartnerCode)).then(() => {
      closePopover();
    });
  };

  /** If enter is pressed and there are companies, then select the first company from the list. */
  const onCompanySearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key !== "Enter" || !companies[0]) return;
    event.preventDefault();
    onSelectCompany(companies[0]);
  };

  const onCompanyNameSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCompanySearchLoading(true);
    UserService.searchUserCompanies(event.target.value)
      .then((resultCompanies) => {
        setIsCompanySearchLoading(false);
        setCompanies(resultCompanies);
      })
      .catch(() => {
        setIsCompanySearchLoading(false);
        setCompanies([]);
      });
  };

  return (
    <div className="relative">
      <Button
        className="uppercase italic"
        color="neutral-700"
        onClick={toggleOpen}
        variant="text"
      >
        ( {company.FullName}
        {company.Currency && ` (${company.Currency})`}
        <FontAwesomeIcon icon={faCaretDown} />)
      </Button>
      {isOpen && (
        <Popover onClose={closePopover}>
          <div className="bg-neutral-200 flex justify-between px-3.5 py-2">
            <h3 className="uppercase">{t("LABEL_COMPANYSEARCH")}</h3>
            <Link className="uppercase ">{t("LABEL_VIEWCURRENTCOMPANY")}</Link>
          </div>
          <div>
            {userIsEmployee && (
              <WithDebounce debounceTime={800} onChange={onCompanyNameSearch}>
                <CompanyPickerSearchInput
                  className="p-3.5"
                  fullWidth
                  id="company-search"
                  onKeyDown={onCompanySearchKeyDown}
                  placeholder={t("LABEL_COMPANYSEARCH_INPUT")}
                />
              </WithDebounce>
            )}
            {isActive && (
              <>
                <div className="bold text-neutral-700 w-full bg-neutral-200 border-y border-neutral-300 py-[0.625rem] px-3.5 flex justify-between">
                  {t("TEXT_NAME")}
                  {isCompanySearchLoading ? (
                    <span className="relative text-transparent w-4 h-4 after:animate-spin after:block after:w-4 after:h-4 after:absolute after:top-[calc(50%-(1em/2))] after:left-[calc(50%-(1em/2))] after:border-2 after:border-solid after:rounded-full after:border-neutral-0 after:border-r-transparent" />
                  ) : (
                    <span>
                      {t("TEXT_ENTRIES")}: {companies.length}
                    </span>
                  )}
                </div>
                <ul className="max-h-96 overflow-x-auto">
                  {companies.map((company: Company) => (
                    <li
                      className="border-b px-3.5 py-[0.625rem] hover:bg-neutral-300 cursor-pointer flex justify-between"
                      key={company.PartnerCode}
                      onClick={() => {
                        onSelectCompany(company);
                      }}
                    >
                      {company.FullName}
                      <span className="flex items-start gap-1">
                        {company.PartnerCode}{" "}
                        {company.Currency && (
                          <span className="text-[0.625rem] leading-4">
                            ({company.Currency})
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Popover>
      )}
    </div>
  );
};

export default CompanyPicker;
